import { ExternalLink, PanelRightClose } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle
} from "@/components/ui/sheet";

import type { ForeignNationalDocument } from "../types";
import {
  DocumentQuickPreview,
  type CaseFilePreviewSource
} from "./DocumentQuickPreview";

type Props = {
  open: boolean;
  foreignNationalId: string;
  document: ForeignNationalDocument | null;
  /** Preview a file that lives only in one of the client's cases. */
  caseFile?: CaseFilePreviewSource | null;
  /**
   * Firm only: open the client document in the Case IDE's viewers and
   * editors; every save is a new version.
   */
  editable?: boolean;
  /** A save or restore changed the document (refetch the list). */
  onDocumentChange?: (document: ForeignNationalDocument) => void;
  /** Shows "Open in Case" (e.g. for a case file). */
  onOpenInCase?: () => void;
  onOpenChange: (open: boolean) => void;
};

/**
 * Right-hand sheet that opens a client document: in the viewers and editors
 * for the firm, a PDF / image preview otherwise.
 */
export function ForeignNationalDocumentQuickPreviewDrawer({
  open,
  foreignNationalId,
  document,
  caseFile = null,
  editable = false,
  onDocumentChange,
  onOpenInCase,
  onOpenChange
}: Props) {
  const filename = document?.filename ?? "Untitled document";
  const [dirty, setDirty] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const documentId = document?.foreign_national_document_id ?? null;

  useEffect(() => {
    setDirty(false);
    setConfirmClose(false);
  }, [documentId, open]);

  const requestOpenChange = useCallback(
    (next: boolean) => {
      if (!next && dirty) {
        setConfirmClose(true);
        return;
      }
      onOpenChange(next);
    },
    [dirty, onOpenChange]
  );
  const discardAndClose = useCallback(() => {
    setConfirmClose(false);
    setDirty(false);
    onOpenChange(false);
  }, [onOpenChange]);

  return (
    <Sheet open={open} onOpenChange={requestOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className="flex flex-col gap-0 p-0 data-[side=right]:w-[95vw] data-[side=right]:sm:max-w-225"
      >
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Close quick preview"
            onClick={() => requestOpenChange(false)}
          >
            <PanelRightClose />
          </Button>
          <div className="flex min-w-0 flex-1 flex-col">
            <SheetTitle className="text-sm font-medium">
              Quick Preview
            </SheetTitle>
            <SheetDescription className="truncate text-xs">
              {filename}
            </SheetDescription>
          </div>
          {onOpenInCase ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={onOpenInCase}
              data-testid="quick-preview-open-in-case"
            >
              <ExternalLink />
              Open in Case
            </Button>
          ) : null}
        </div>

        <DocumentQuickPreview
          foreignNationalId={foreignNationalId}
          document={document}
          caseFile={caseFile}
          enabled={open}
          editable={editable}
          onDocumentChange={onDocumentChange}
          onDirtyChange={setDirty}
        />
      </SheetContent>

      <AlertDialog open={confirmClose} onOpenChange={setConfirmClose}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard your changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Nothing has been saved to {filename}. Your edits will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Editing</AlertDialogCancel>
            <AlertDialogAction onClick={discardAndClose}>
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
